import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Circle, Cpu, Menu } from "lucide-react";
import { DEFAULT_ORGANIZATION_NAME, FOUNDER_FULL_NAME } from "../../config/vertical";
import { EXECUTION_MODE, isCommercialContext } from "../../config/environment";
import { breadcrumbFor } from "../../lib/copy";
import { T } from "../../lib/testids";

export default function TopBar({ onMenuClick }) {
  const [clock, setClock] = useState(new Date());
  const location = useLocation();

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const crumb = breadcrumbFor(location.pathname);

  // Un Socio Fundador nunca ve "Demo". Internamente, development muestra un
  // punto discreto (no la palabra "Demo") y demo_internal es la única
  // etiqueta visible, reservada al equipo.
  const showDemoLabel = !isCommercialContext() && EXECUTION_MODE === "demo_internal";
  const showDevDot = !isCommercialContext() && EXECUTION_MODE === "development";

  // Sin nombre de Socio Fundador configurado, "Director Comercial" es el
  // operador genérico de laboratorio — nunca debe llegar así a un despliegue
  // real. La esquina solo muestra su nombre — "Socio Fundador" es un programa
  // comercial, no una etiqueta permanente de la interfaz.
  const identityName = FOUNDER_FULL_NAME || "Director Comercial";
  const identityInitials = FOUNDER_FULL_NAME
    ? FOUNDER_FULL_NAME.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase()
    : "DC";

  return (
    <header
      data-testid={T.topbar}
      className="h-14 border-b border-white/[0.06] bg-[#0d1321]/95 backdrop-blur flex items-center justify-between gap-3 px-3 sm:px-6 sticky top-0 z-20"
    >
      <div className="flex items-center gap-2 sm:gap-3 text-xs text-zinc-500 min-w-0">
        <button
          type="button"
          data-testid={T.topbarMenuToggle}
          onClick={onMenuClick}
          aria-label="Abrir menú"
          className="lg:hidden shrink-0 w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-100"
        >
          <Menu size={18} />
        </button>
        {/* Acceso explícito a Inicio, con TEXTO (no solo un ícono que hay
            que adivinar) — siempre visible en toda pantalla, incluida el
            detalle de una oportunidad, en móvil y escritorio, sin
            depender de abrir el menú lateral primero. */}
        <Link
          to="/"
          data-testid={T.topbarHomeLink}
          aria-label="Ir a Inicio"
          className="shrink-0 flex items-center gap-1.5 h-8 pl-1.5 pr-3 rounded-full bg-amber-500 text-black hover:bg-amber-400 transition-colors font-mono text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em]"
        >
          <Cpu size={14} strokeWidth={2.5} />
          Inicio
        </Link>
        <span className="hidden sm:inline font-mono uppercase tracking-[0.2em] shrink-0">{crumb}</span>
        <ChevronRight size={12} className="hidden sm:block shrink-0" />
        <span className="text-zinc-300 font-mono uppercase tracking-[0.15em] truncate min-w-0">
          {DEFAULT_ORGANIZATION_NAME}
        </span>
      </div>

      <div className="flex items-center gap-3 sm:gap-6 text-xs shrink-0">
        {showDemoLabel && (
          <div className="hidden md:flex items-center gap-2 text-zinc-500">
            <Circle size={7} className="fill-emerald-500 text-emerald-500 pulse-dot" />
            <span className="font-mono uppercase tracking-[0.15em]">Demo interno</span>
          </div>
        )}
        {showDevDot && (
          <div
            className="hidden md:block w-2 h-2 rounded-full bg-zinc-700"
            title="Entorno de desarrollo local"
            aria-label="Entorno de desarrollo local"
          />
        )}
        <div
          className="hidden md:block text-zinc-400 font-mono tabular-nums"
          data-testid={T.topbarClock}
        >
          {clock.toLocaleTimeString("es-MX", { hour12: false })}
        </div>
        <div className="flex items-center gap-2 sm:pl-4 sm:border-l sm:border-white/[0.06]">
          <div className="w-7 h-7 rounded-full bg-amber-500/12 flex items-center justify-center text-amber-400 text-[10px] font-mono font-bold shrink-0">
            {identityInitials}
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="text-[11px] text-zinc-200 font-medium">{identityName}</div>
          </div>
        </div>
      </div>
    </header>
  );
}
